/**
 * The studio's one header: the name of what is playing, and — in a scored room — the score beside
 * it in the same bar, so the name and the score never stack two bars on a phone.
 *
 * It is a HEADER, not an overlay: it measures itself and publishes the strip as
 * `--orbiters-top-chrome`, and the studio (and the room's chat rail) reserve exactly that much, the
 * same way they already make room for the dock. Zero when there is nothing to show — an inline
 * session has no name and no score — so the reserve costs nothing there.
 */
import { lazy, Suspense, useCallback, useEffect, useRef } from 'react';
import { ScoreHeader, type ScoreHeaderTitle } from 'plantasia.space-design/react';

export const TOP_CHROME_VAR = '--orbiters-top-chrome';

function NameOnly({ title }: { title?: ScoreHeaderTitle }) {
  return title ? <ScoreHeader title={title} /> : null;
}

// Only a room has a score, so only a room pays for the code that reads one. A chunk that fails to
// load leaves the name on its own rather than taking the studio down with it — room chrome failing
// must never end the session.
const RoomScoreHeader = lazy(() =>
  import('../../../room/RoomScoreHeader').catch((error) => {
    console.warn('[room] score header failed to load', error);
    return { default: NameOnly };
  }),
);

export interface StudioHeaderProps {
  title: ScoreHeaderTitle | null;
  roomActive: boolean;
  /** Where the bar starts: past the landscape rail, which owns the full height of the left edge. */
  left: string | number;
  /** The studio's chrome-theme registration, so the bar paints with the same preset as the dock. */
  chromeRef: (el: HTMLElement | null) => (() => void) | undefined;
}

export function StudioHeader({ title, roomActive, left, chromeRef }: StudioHeaderProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const setHost = useCallback(
    (el: HTMLDivElement | null) => {
      hostRef.current = el;
      return chromeRef(el);
    },
    [chromeRef],
  );

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const root = document.documentElement;
    const write = () => root.style.setProperty(TOP_CHROME_VAR, `${host.offsetHeight}px`);
    write();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(write);
    observer?.observe(host);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(TOP_CHROME_VAR);
    };
  }, []);

  const nameOnly = <NameOnly title={title ?? undefined} />;

  return (
    <div
      ref={setHost}
      data-studio-header=""
      // Inside the studio's own layer (z 41), so a dialog still covers it; 45 is the drawer's rung,
      // above every stage and its overlays.
      style={{ position: 'fixed', top: 0, left, right: 0, zIndex: 45, pointerEvents: 'auto' }}
    >
      {roomActive ? (
        <Suspense fallback={nameOnly}>
          <RoomScoreHeader title={title ?? undefined} />
        </Suspense>
      ) : (
        nameOnly
      )}
    </div>
  );
}
